import React, {
    ComponentType,
    createContext,
    FunctionComponent,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useState,
    useRef
} from 'react';
import { AuthenticationProvider, LoadingStatus } from '@paycor/pacl';
import { Redirect } from 'react-router-dom';
import { PermissionsApiClient, PermissionsApiResponse } from '../apiClient';
import { PendoService } from '../utilities/pendo.service';
import Forbidden from '../views/common/forbidden/Forbidden';
import Limitedclient0 from '../views/common/limited-client0/limitedclient0';
import { RuntimeContext } from './RuntimeContext';
import { AuthService, User } from './interfaces/AuthService';

export interface AuthenticatedUserContextType {
    admin: boolean;
    loading: boolean;
    participant: boolean;
    clientId: string;
    clientName: string;
    token: string;
    companyId: string;
    limitedClient0: boolean;
}

export const AuthenticatedUserContext = createContext<AuthenticatedUserContextType>({
    admin: false,
    loading: true,
    participant: false,
    clientId: '',
    clientName: '',
    token: '',
    companyId: '',
    limitedClient0: false
});

// We don't want Pendo Service to be called more than once that's why.
let pendoCalling = false;

export const AuthenticatedUserContextProvider: FunctionComponent<{
    children: ReactNode | ReactNode[];
}> = ({ children }) => {
    const config = useContext(RuntimeContext);
    const authService = useContext(AuthenticationProvider.Context);
    const apiClient = useMemo(
        () => new PermissionsApiClient(authService as AuthService, config.app.ui.urlBase),
        [authService, config.app.ui.urlBase]
    );

    // Pendo Initialization
    const pendoService = useMemo(
        () =>
            new PendoService({
                pendoApiKey: config.pendoApiKey
            }),
        [config.pendoApiKey]
    );

    const [authenticatedUserDetails, setAuthenticatedUserDetails] = useState<AuthenticatedUserContextType>({
        admin: false,
        loading: true,
        participant: false,
        clientId: '',
        clientName: '',
        token: '',
        companyId: '',
        limitedClient0: false
    });

    const [errorCode, setErrorCode] = useState<string>('');
    const [isLimitedClient0, setIsLimitedClient0] = useState<boolean>(false);
    const [user, setUser] = useState<User | undefined>(undefined);
    const intervalRef = useRef(null);

    useEffect(() => {
        let isSubscribed = true;

        (async () => {
            try {
                const permissions = await apiClient.getPermissions();
                const user = await (authService as AuthService).getUser();
                setIsLimitedClient0(permissions.limitedClient0);
                setUser(user);

                if (isSubscribed) {
                    setAuthenticatedUserDetails({
                        loading: false,
                        ...permissions,
                        admin: Boolean(permissions.admin),
                        token: user?.access_token
                    });

                    if (!pendoCalling) {
                        pendoService.initializePendoForPathsInitialize({
                            clientId: permissions.clientId,
                            clientName: permissions.clientName
                        });
                        pendoCalling = true;
                    }
                }
            } catch (error: unknown) {
                if (error instanceof Error && error.message === '403') {
                    setErrorCode('403');
                }
            }
        })();

        return () => {
            isSubscribed = false;
        };
    }, [apiClient, setAuthenticatedUserDetails, pendoService, authService, user]);

    useEffect(() => {
        let interval;
        // renewInterval if user exists and not already set
        if (user && !intervalRef.current) {
            interval = setInterval(async () => {
                if (user && user.expires_at) {
                    const now = new Date();
                    const expAt = new Date(user.expires_at * 1000);
                    if (now >= expAt) {
                        const newUser = await (authService as AuthService).getUser();
                        if (newUser && newUser.access_token !== user.access_token) {
                            setUser(newUser);
                            const permissions = await apiClient.getPermissions();
                            setAuthenticatedUserDetails({
                                loading: false,
                                ...permissions,
                                admin: Boolean(permissions.admin),
                                token: newUser?.access_token
                            });
                        }
                    }
                }
            }, 5000);
            intervalRef.current = interval;
        }

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [user, apiClient, authService, setAuthenticatedUserDetails]);

    if (isLimitedClient0) {
        return <Limitedclient0 />;
    }

    if (errorCode === '403') {
        return <Forbidden />;
    }

    return (
        <AuthenticatedUserContext.Provider value={authenticatedUserDetails}>
            {children}
        </AuthenticatedUserContext.Provider>
    );
};

export const AuthenticatedUserContextConsumer: FunctionComponent<{
    children: (value: PermissionsApiResponse) => ReactNode | ReactNode[];
}> = AuthenticatedUserContext.Consumer;

export function requirePathsAdmin(Component: ComponentType): FunctionComponent<any> {
    return (props) => {
        const context = useContext(AuthenticatedUserContext);

        if (context.loading) {
            return <LoadingStatus />;
        }

        if (context.admin) {
            return <Component {...props} />;
        }

        return <Redirect to={{ pathname: '/forbidden' }} />;
    };
}

export function requirePathsParticipant(Component: ComponentType): FunctionComponent<{
    children: (value: PermissionsApiResponse) => ReactNode | ReactNode[];
}> {
    return (props) => {
        const context = useContext(AuthenticatedUserContext);

        if (context.loading) {
            return <LoadingStatus />;
        }

        if (context.participant) {
            return <Component {...props} />;
        }

        return <Redirect to={{ pathname: '/forbidden' }} />;
    };
}
